import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ClientEditModal = ({
    isOpen,
    onClose,
    editForm,
    editingTour,
    handleEditChange,
    handleEditSubmit,
    propertyTypes,
    cities,
    areas,
    handleDeleteImage,
    handleAddImages,
    supportedCurrencies,
    handleAddSubarea
}) => {
    const { t, i18n } = useTranslation();
    const [showAddArea, setShowAddArea] = useState(false);
    const [newAreaAr, setNewAreaAr] = useState('');
    const [newAreaEn, setNewAreaEn] = useState('');
    const [isAddingArea, setIsAddingArea] = useState(false);
    const [addAreaError, setAddAreaError] = useState('');

    const existingSubareas = cities?.find(c => c.en === editForm.city)?.subareas || [];
    const normalizedNewAr = newAreaAr.trim().toLowerCase();
    const normalizedNewEn = newAreaEn.trim().toLowerCase();

    const similarAreas = (newAreaAr.trim() || newAreaEn.trim())
        ? existingSubareas.filter(sub => {
            const arMatch = newAreaAr.trim() && sub.ar?.toLowerCase().includes(normalizedNewAr);
            const enMatch = newAreaEn.trim() && sub.en?.toLowerCase().includes(normalizedNewEn);
            return arMatch || enMatch;
          })
        : [];

    const isDuplicate = existingSubareas.some(sub => 
        sub.ar?.trim().toLowerCase() === normalizedNewAr ||
        (newAreaEn.trim() && sub.en?.trim().toLowerCase() === normalizedNewEn)
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-bg-card border border-gold/20 rounded-lg shadow-xl max-w-2xl w-full flex flex-col" style={{ maxHeight: '90vh' }}>
                {/* Fixed Header */}
                <div className="flex justify-between items-center p-6 border-b border-gold/10">
                    <h3 className="text-xl font-bold text-text">{t('editTour')}</h3>
                    <button onClick={onClose} className="text-text-muted hover:text-white">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable Form Content */}
                <form onSubmit={handleEditSubmit} className="flex flex-col flex-1 overflow-hidden">
                    <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-text-muted">{t('propertyName')} <span className="text-danger-500">*</span></label>
                                <input type="text" name="title" value={editForm.title} onChange={handleEditChange} className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-muted">{t('propertyId')}</label>
                                <input 
                                    type="text" 
                                    name="property_id" 
                                    value={editForm.property_id} 
                                    onChange={handleEditChange} 
                                    className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold" 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-muted">{t('currency')}</label>
                                <select name="currency" value={editForm.currency} onChange={handleEditChange} className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold">
                                    {supportedCurrencies && supportedCurrencies.length > 0 ? (
                                        (supportedCurrencies || []).map(currency => (
                                            <option key={currency} value={currency}>{t(`currency${currency}`) || currency}</option>
                                        ))
                                    ) : (
                                        <option value="">{t('selectCurrency')}</option>
                                    )}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-muted">{t('price')}</label>
                                <input type="number" name="price" value={editForm.price} onChange={handleEditChange} className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-muted">{t('propertyType')}</label>
                                <select name="property_type" value={editForm.property_type} onChange={handleEditChange} className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold">
                                    <option value="">{t('selectPropertyType')}</option>
                                    {(propertyTypes || []).map(type => (
                                        <option key={type.en} value={type.en}>{i18n.language === 'ar' ? type.ar : (i18n.language === 'es' && type.es ? type.es : type.en)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted">{t('size')}</label>
                            <input type="text" name="size" value={editForm.size} onChange={handleEditChange} className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted">{t('rooms')}</label>
                            <input type="number" name="rooms" value={editForm.rooms} onChange={handleEditChange} className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold" min="0" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted">{t('floor')}</label>
                            <input type="number" name="floor" value={editForm.floor} onChange={handleEditChange} className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted">{t('city')}</label>
                            <select name="city" value={editForm.city} onChange={handleEditChange} className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold">
                                <option value="">{t('selectCity')}</option>
                                {(cities || []).map(city => (
                                    <option key={city.en} value={city.en}>{i18n.language === 'ar' ? city.ar : (i18n.language === 'es' && city.es ? city.es : city.en)}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-sm font-medium text-text-muted">{t('area')}</label>
                                {editForm.city && !showAddArea && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAddArea(true)}
                                        className="text-xs text-gold hover:underline"
                                    >
                                        + {i18n.language === 'ar' ? 'إضافة منطقة جديدة' : 'Add new area'}
                                    </button>
                                )}
                            </div>
                            {showAddArea ? (
                                <div className="p-3 bg-bg-soft border border-gold/20 rounded-md space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[10px] font-medium text-text-muted mb-0.5">
                                                {i18n.language === 'ar' ? 'الاسم بالعربية' : 'Name in Arabic'}
                                            </label>
                                            <input
                                                type="text"
                                                value={newAreaAr}
                                                onChange={(e) => setNewAreaAr(e.target.value)}
                                                className="w-full bg-bg-card border border-gold/20 text-text rounded-md py-1 px-2 text-xs focus:ring-gold focus:border-gold"
                                                placeholder="الميدان"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-medium text-text-muted mb-0.5">
                                                {i18n.language === 'ar' ? 'الاسم بالإنجليزية' : 'Name in English'}
                                            </label>
                                            <input
                                                type="text"
                                                value={newAreaEn}
                                                onChange={(e) => setNewAreaEn(e.target.value)}
                                                className="w-full bg-bg-card border border-gold/20 text-text rounded-md py-1 px-2 text-xs focus:ring-gold focus:border-gold"
                                                placeholder="Midan"
                                            />
                                        </div>
                                    </div>
                                    {similarAreas.length > 0 && (
                                        <div className="text-[10px] text-text-muted bg-bg-card/50 p-2 border border-gold/10 rounded">
                                            <span className="font-semibold block mb-1">
                                                {i18n.language === 'ar' ? 'المناطق الموجودة المشابهة:' : 'Similar existing areas:'}
                                            </span>
                                            <div className="flex flex-wrap gap-1">
                                                {similarAreas.map(sub => (
                                                    <span key={sub.en} className="bg-bg-soft px-1.5 py-0.5 rounded text-gold border border-gold/10">
                                                        {i18n.language === 'ar' ? sub.ar : sub.en}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {isDuplicate && (
                                        <p className="text-[10px] text-danger-500">
                                            {i18n.language === 'ar' ? 'هذه المنطقة موجودة بالفعل' : 'This area already exists'}
                                        </p>
                                    )}
                                    {addAreaError && <p className="text-[10px] text-danger-500">{addAreaError}</p>}
                                    <div className="flex justify-end gap-2 text-xs">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowAddArea(false);
                                                setNewAreaAr('');
                                                setNewAreaEn('');
                                                setAddAreaError('');
                                            }}
                                            className="px-2 py-0.5 border border-gold/20 rounded text-text-muted hover:bg-gold/10 text-xs"
                                        >
                                            {t('cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isAddingArea || !newAreaAr.trim() || isDuplicate}
                                            onClick={async () => {
                                                try {
                                                    setIsAddingArea(true);
                                                    setAddAreaError('');
                                                    const added = await handleAddSubarea(editForm.city, newAreaAr, newAreaEn);
                                                    handleEditChange({
                                                        target: {
                                                            name: 'area',
                                                            value: added.en
                                                        }
                                                    });
                                                    setShowAddArea(false);
                                                    setNewAreaAr('');
                                                    setNewAreaEn('');
                                                } catch (err) {
                                                    setAddAreaError(err.response?.data?.detail || 'Failed to add area');
                                                } finally {
                                                    setIsAddingArea(false);
                                                }
                                            }}
                                            className="bg-gold text-white px-2 py-0.5 rounded hover:opacity-90 disabled:opacity-50 text-xs"
                                        >
                                            {isAddingArea ? '...' : (i18n.language === 'ar' ? 'حفظ' : 'Save')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                cities?.find(c => c.en === editForm.city)?.subareas?.length > 0 ? (
                                    <select
                                        name="area"
                                        value={editForm.area}
                                        onChange={handleEditChange}
                                        className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                                    >
                                        <option value="">{t('selectArea') || 'Select Area'}</option>
                                        {cities.find(c => c.en === editForm.city).subareas.map(sub => (
                                            <option key={sub.en} value={sub.en}>
                                                {i18n.language === 'ar' ? sub.ar : (i18n.language === 'es' && sub.es ? sub.es : sub.en)}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="area"
                                            value={editForm.area}
                                            onChange={handleEditChange}
                                            className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                                            list="edit-areas-list"
                                            placeholder={editForm.city ? (i18n.language === 'ar' ? "اكتب المنطقة أو أضفها" : "Type area or add it") : ""}
                                        />
                                        {areas.length > 0 && (
                                            <datalist id="edit-areas-list">
                                                {(areas || []).map(area => <option key={area} value={area} />)}
                                            </datalist>
                                        )}
                                    </div>
                                )
                            )}
                        </div>

                        <div className="flex gap-4">
                            <div className="flex items-center">
                                <input type="checkbox" name="has_solar" checked={editForm.has_solar} onChange={handleEditChange} className="h-4 w-4 text-gold border-gold/30 bg-bg-card rounded focus:ring-gold" />
                                <label className="ml-2 block text-sm text-text-muted">{t('hasSolar')}</label>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted">{t('description')}</label>
                            <textarea name="description" value={editForm.description} onChange={handleEditChange} rows="3" className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"></textarea>
                            <p className="text-xs text-text-muted mt-1 italic">
                                {i18n.language === 'ar' ? 'استخدم **النص** لجعل الخط عريضاً' : 'Use **text** for bold'}
                            </p>
                        </div>

                        {/* Image Management Section */}
                        <div className="col-span-2 pt-4 border-t border-gold/10">
                            <h4 className="text-md font-semibold text-text mb-3">{t('manageImages') || "Manage Images"}</h4>

                            {/* Existing Images Gallery */}
                            {editingTour?.property_images && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                    {JSON.parse(editingTour.property_images).map((img, idx) => (
                                        <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-gold/20">
                                            <img src={`${import.meta.env.VITE_API_URL || ''}/static/${img}`} alt={`Property ${idx}`} className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteImage(img)}
                                                className="absolute top-1 right-1 bg-danger-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                title={t('deleteImage')}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Add New Images */}
                            <div>
                                <label className="block text-sm font-medium text-text-muted mb-1">{t('addMoreImages') || "Add More Images"}</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleAddImages}
                                    className="block w-full text-sm text-text-muted file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gold-gradient file:text-white hover:file:opacity-90"
                                />
                            </div>
                        </div>

                        {/* Open Graph Section */}
                        <div className="col-span-2 pt-4 border-t border-gold/10">
                            <h4 className="text-md font-semibold text-text mb-3">{t('socialSharingMetadata')}</h4>
                            <p className="text-sm text-text-muted mb-4">{t('socialSharingDescription')}</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-text-muted">
                                        {t('ogTitle')}
                                        <span className="text-text-muted/50 font-normal ml-1">({t('optional')})</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="og_title"
                                        value={editForm.og_title}
                                        onChange={handleEditChange}
                                        placeholder={editForm.title}
                                        className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold placeholder-textMuted/50"
                                    />
                                    <p className="text-xs text-text-muted mt-1">{t('ogTitleHint')}</p>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-text-muted">
                                        {t('ogDescription')}
                                        <span className="text-text-muted/50 font-normal ml-1">({t('optional')})</span>
                                    </label>
                                    <textarea
                                        name="og_description"
                                        value={editForm.og_description}
                                        onChange={handleEditChange}
                                        rows="2"
                                        placeholder={editForm.description || editForm.title}
                                        className="mt-1 block w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold placeholder-textMuted/50"
                                    ></textarea>
                                    <p className="text-xs text-text-muted mt-1">{t('ogDescriptionHint')}</p>
                                </div>
                                {/* og_image input removed - using thumbnail automatically */}
                            </div>
                        </div>

                        <div className="col-span-2">
                            <div className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    id="editShowPrice"
                                    name="show_price"
                                    checked={editForm.show_price}
                                    onChange={handleEditChange}
                                    className="h-5 w-5 text-gold border-gold/30 bg-bg-card rounded focus:ring-gold"
                                />
                                <label htmlFor="editShowPrice" className="text-sm font-medium text-text-muted">
                                    {t('showPrices')}
                                </label>
                            </div>
                        </div>

                    </div>

                    {/* Fixed Footer */}
                    <div className="flex justify-end gap-3 p-6 border-t border-gold/10 bg-bg-card shrink-0">
                        <button type="button" onClick={onClose} className="bg-transparent py-2 px-4 border border-gold/20 rounded-md shadow-sm text-sm font-medium text-text-muted hover:bg-gold/10 hover:text-gold transition-colors">
                            {t('cancel')}
                        </button>
                        <button type="submit" className="bg-gold-gradient text-white border-none py-2 px-4 rounded-md shadow-sm text-sm font-medium">
                            {t('save')}
                        </button>
                    </div>
                </form>
            </div >
        </div >
    );
};

export default ClientEditModal;
